import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { StepConsents } from "@/components/auth/StepConsents";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Building2 } from "lucide-react";
import { JoinProfileForm } from "./join/JoinProfileForm";
import { JoinLoading, JoinInvalid, JoinDone } from "./join/JoinStates";

interface InvitationData {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  grade: string | null;
  organization_id: string;
  status: string;
  expires_at: string;
  organizations?: { name: string; type: string } | null;
}

type JoinStep = "loading" | "invalid" | "complete_profile" | "consents" | "done";

export default function Join() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get("token");

  const [joinStep, setJoinStep] = useState<JoinStep>("loading");
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [invalidReason, setInvalidReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Profile form
  const [password, setPassword] = useState("");
  const [profession, setProfession] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");

  // Consents
  const [consents, setConsents] = useState({
    consent_rgpd: false,
    consent_cgu: false,
    consent_droits_auteur: false,
  });

  const [isNewUser, setIsNewUser] = useState(true);

  // ── Load invitation from token ──
  useEffect(() => {
    if (!token) {
      setInvalidReason("Aucun token d'invitation trouvé dans l'URL.");
      setJoinStep("invalid");
      return;
    }

    (async () => {
      const { data: rpcData, error: rpcError } = await supabase
        .rpc("get_invitation_by_token", { p_token: token });

      if (rpcError || !rpcData || rpcData.length === 0) {
        setInvalidReason("Lien d'invitation introuvable ou invalide.");
        setJoinStep("invalid");
        return;
      }

      const invRow = rpcData[0];

      const { data: orgRows } = await supabase
        .rpc("get_org_info_by_invitation_token", { p_token: token });
      const orgInfo = orgRows?.[0] ?? null;

      const data: InvitationData = {
        ...invRow,
        organizations: orgInfo ? { name: orgInfo.org_name, type: orgInfo.org_type } : null,
      };

      if (data.status === "accepted") {
        setInvalidReason("Ce lien a déjà été utilisé.");
        setJoinStep("invalid");
        return;
      }

      if (data.status === "revoked") {
        setInvalidReason("Cette invitation a été révoquée. Contactez votre administrateur.");
        setJoinStep("invalid");
        return;
      }

      if (new Date(data.expires_at) < new Date()) {
        setInvalidReason("Ce lien d'invitation a expiré (validité 72h). Demandez un nouveau lien.");
        setJoinStep("invalid");
        return;
      }

      setInvitation(data);

      const { data: sessionData } = await supabase.auth.getSession();
      const sessionEmail = sessionData.session?.user?.email?.toLowerCase();
      const inviteEmail = (data.email ?? "").toLowerCase();

      if (sessionData.session && sessionEmail === inviteEmail) {
        setIsNewUser(false);
        setJoinStep("consents");
      } else {
        if (sessionData.session && sessionEmail !== inviteEmail) {
          await supabase.auth.signOut();
        }
        setIsNewUser(true);
        setJoinStep("complete_profile");
      }
    })();
  }, [token]);

  const handleCreateAndJoin = () => {
    if (!invitation) return;
    if (password.length < 8) {
      toast({ title: "Mot de passe trop court (min. 8 caractères)", variant: "destructive" });
      return;
    }
    setJoinStep("consents");
  };

  const handleAccept = async () => {
    if (!invitation || !token) return;
    setLoading(true);

    try {
      const { data: result, error: acceptError } = await supabase.functions.invoke("accept-invitation", {
        body: {
          token,
          profession,
          country,
          phone,
          password: isNewUser ? password : undefined,
          consent_rgpd: consents.consent_rgpd,
          consent_cgu: consents.consent_cgu,
          consent_droits_auteur: consents.consent_droits_auteur,
        },
      });

      if (acceptError) throw acceptError;
      if (result?.error) throw new Error(result.error);

      if (isNewUser && password) {
        const { error: loginError } = await supabase.auth.signInWithPassword({
          email: invitation.email,
          password,
        });
        if (loginError) {
          toast({
            title: "Compte activé",
            description: "Connectez-vous avec votre nouveau mot de passe.",
          });
          navigate(`/auth?email=${encodeURIComponent(invitation.email)}`);
          return;
        }
      }

      setJoinStep("done");
      setTimeout(() => navigate("/hub"), 2000);
    } catch (err: unknown) {
      toast({ title: "Erreur", description: getErrorMessage(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const orgData = invitation?.organizations ?? null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary text-primary-foreground font-bold text-2xl mb-4">
            S
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Smart-EFI</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Rejoindre une organisation</CardTitle>
            <CardDescription>
              Complétez votre inscription via votre lien d'invitation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {joinStep === "loading" && <JoinLoading />}
            {joinStep === "invalid" && <JoinInvalid reason={invalidReason} />}
            {joinStep === "done" && <JoinDone />}

            {joinStep === "complete_profile" && invitation && (
              <JoinProfileForm
                invitation={invitation}
                orgData={orgData}
                password={password}
                setPassword={setPassword}
                profession={profession}
                setProfession={setProfession}
                country={country}
                setCountry={setCountry}
                phone={phone}
                setPhone={setPhone}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                onContinue={handleCreateAndJoin}
              />
            )}

            {joinStep === "consents" && (
              <div className="space-y-4">
                {invitation && (
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
                    <Building2 className="w-5 h-5 text-primary shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{orgData?.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">Rôle : {invitation.role}</p>
                    </div>
                  </div>
                )}

                <StepConsents
                  data={consents}
                  onChange={(field, value) => setConsents((prev) => ({ ...prev, [field]: value }))}
                />

                <Button
                  onClick={handleAccept}
                  className="w-full"
                  disabled={!consents.consent_rgpd || !consents.consent_cgu || !consents.consent_droits_auteur || loading}
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Activation...</>
                  ) : (
                    "Activer mon compte"
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
